import type { QueryClient } from "@tanstack/react-query";
import { Link, Outlet, createRootRouteWithContext, useLocation, useRouter } from "@tanstack/react-router";
import { useStaleChunkReload } from "@seagames/ui";
import { useTranslation } from "react-i18next";
import mascotUrl from "../assets/brand/dashboard-mascot-wave.webp";
import { AuthCard, AuthSplit } from "../components/auth/auth-split";
import { errorMessage, isTransientError, supportRequestIdOf } from "../lib/api/messages";

export interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: () => <Outlet />,
	notFoundComponent: NotFound,
	errorComponent: RouteError,
});

/**
 * The error pages wear the sign-in page's brand (`AuthSplit`): the navy half
 * with the logo, slogan, mascot and KL skyline, and the card on the light
 * half. On a phone the split hides its mascot, so the card shows the waving
 * one instead. Both pages render outside the app shell (an unknown address
 * matches no layout), so the full-screen frame is always right.
 */
function Shell({ code, children }: { code: string; children: React.ReactNode }) {
	return (
		<AuthSplit>
			<AuthCard>
				<div className="flex items-end justify-between gap-[1rem]">
					<span className="grid gap-[0.375rem]">
						<span className="font-display font-bold text-[3.5rem] text-gms-navy leading-[0.85] tracking-[0.01em] min-[45rem]:text-[4.5rem]">{code}</span>
						<span aria-hidden className="block h-[0.25rem] w-[5.5rem] rounded-[0.125rem] bg-linear-to-r from-gms-nav-red to-gms-nav-yellow" />
					</span>
					<img
						src={mascotUrl}
						alt=""
						aria-hidden
						decoding="async"
						className="pointer-events-none h-[6.5rem] w-auto select-none min-[45rem]:h-[7.5rem] lg:hidden"
					/>
				</div>
				{children}
			</AuthCard>
		</AuthSplit>
	);
}

/** "Go back" when there is somewhere to go back to inside the app; otherwise the dashboard. */
function useGoBack() {
	const router = useRouter();
	return () => {
		if (typeof window !== "undefined" && window.history.length > 1) router.history.back();
		else void router.navigate({ to: "/" });
	};
}

function NotFound() {
	const { t } = useTranslation("nav");
	const { pathname } = useLocation();
	const goBack = useGoBack();
	return (
		<Shell code={t("errors.notFoundCode")}>
			<h2 className="font-bold text-[1.5rem] text-gms-navy leading-[1.15] lg:text-[1.75rem]">{t("errors.notFoundTitle")}</h2>
			<p className="text-gms-ink-2">
				{t("errors.notFoundBody")} {t("errors.notFoundHint")}
			</p>
			<p className="overflow-hidden text-ellipsis whitespace-nowrap rounded-[0.375rem] bg-gms-soft px-[0.75rem] py-[0.5rem] font-mono text-[0.8125rem] text-gms-ink-2" title={pathname}>
				<span className="sr-only">{t("errors.requestedAddress")} </span>
				{pathname}
			</p>
			<div className="flex flex-col gap-[0.625rem] min-[30rem]:flex-row min-[30rem]:flex-wrap">
				<Link to="/" className="btn btn-primary w-full min-[30rem]:w-auto">
					{t("errors.backToDashboard")}
				</Link>
				<button type="button" className="btn btn-outline w-full min-[30rem]:w-auto" onClick={goBack}>
					{t("errors.goBack")}
				</button>
			</div>
		</Shell>
	);
}

/**
 * A route that threw. The distinction that matters here is the same one the
 * whole stack is built around: a dependency that did not answer gets "try
 * again" and KEEPS the session; anything else is a real error. A chunk a
 * deploy removed renders nothing: the page is reloading to the new version.
 */
function RouteError({ error, reset }: { error: Error; reset: () => void }) {
	const reloading = useStaleChunkReload(error);
	if (reloading) return null;
	return <RouteErrorScreen error={error} reset={reset} />;
}

function RouteErrorScreen({ error, reset }: { error: Error; reset: () => void }) {
	const { t } = useTranslation("nav");
	const transient = isTransientError(error);
	const requestId = supportRequestIdOf(error);

	return (
		<Shell code={transient ? t("errors.unreachableCode") : t("errors.errorCode")}>
			<h2 className="font-bold text-[1.5rem] text-gms-navy leading-[1.15] lg:text-[1.75rem]">
				{transient ? t("errors.serverUnreachable") : t("errors.somethingWentWrong")}
			</h2>
			<p className="text-gms-ink-2">{errorMessage(error)}</p>
			{requestId && (
				<p className="text-[0.75rem] text-gms-ink-3">
					{t("errors.requestId")} <code className="font-mono">{requestId}</code>
				</p>
			)}
			<div className="flex flex-col gap-[0.625rem] min-[30rem]:flex-row min-[30rem]:flex-wrap">
				<button type="button" className="btn btn-primary w-full min-[30rem]:w-auto" onClick={reset}>
					{t("errors.tryAgain")}
				</button>
				<Link to="/" className="btn btn-outline w-full min-[30rem]:w-auto">
					{t("errors.dashboard")}
				</Link>
			</div>
		</Shell>
	);
}
